import {
  managedDepartmentListSchema,
  managedDepartmentSchema,
  managedSiteListSchema,
  managedSiteSchema,
  managedTeamListSchema,
  managedTeamSchema,
  okResponseSchema,
  teamMembersResponseSchema,
  type AddTeamMemberRequest,
  type CreateDepartmentRequest,
  type CreateSiteRequest,
  type CreateTeamRequest,
  type OrgListQuery,
  type UpdateDepartmentRequest,
  type UpdateSiteRequest,
  type UpdateTeamRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userLookupsQueryKey, usersQueryKey } from '@/platform/features/users/use-users';
import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const departmentsQueryKey = ['departments'] as const;
export const sitesQueryKey = ['sites'] as const;
export const teamsQueryKey = ['teams'] as const;

export function teamMembersQueryKey(teamId: string) {
  return [...teamsQueryKey, teamId, 'members'] as const;
}

async function invalidateOrgCaches(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: departmentsQueryKey }),
    queryClient.invalidateQueries({ queryKey: sitesQueryKey }),
    queryClient.invalidateQueries({ queryKey: teamsQueryKey }),
    queryClient.invalidateQueries({ queryKey: userLookupsQueryKey }),
    queryClient.invalidateQueries({ queryKey: usersQueryKey }),
  ]);
}

export function useDepartments(query: Partial<OrgListQuery> = {}) {
  const includeInactive = query.includeInactive ?? true;
  return useQuery({
    queryKey: [...departmentsQueryKey, { q: query.q, includeInactive }],
    refetchOnWindowFocus: true,
    queryFn: () =>
      apiFetch(
        `/departments${toSearchParams({
          q: query.q,
          includeInactive,
        })}`,
        managedDepartmentListSchema,
      ),
  });
}

export function useSites(query: Partial<OrgListQuery> = {}) {
  const includeInactive = query.includeInactive ?? true;
  return useQuery({
    queryKey: [...sitesQueryKey, { q: query.q, includeInactive }],
    refetchOnWindowFocus: true,
    queryFn: () =>
      apiFetch(
        `/sites${toSearchParams({
          q: query.q,
          includeInactive,
        })}`,
        managedSiteListSchema,
      ),
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSiteRequest) =>
      apiFetch('/sites', managedSiteSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSiteRequest }) =>
      apiFetch(`/sites/${id}`, managedSiteSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/sites/${id}`, okResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useTeams(query: Partial<OrgListQuery> = {}) {
  const includeInactive = query.includeInactive ?? true;
  return useQuery({
    queryKey: [...teamsQueryKey, { q: query.q, includeInactive, departmentId: query.departmentId }],
    refetchOnWindowFocus: true,
    queryFn: () =>
      apiFetch(
        `/teams${toSearchParams({
          q: query.q,
          includeInactive,
          departmentId: query.departmentId,
        })}`,
        managedTeamListSchema,
      ),
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDepartmentRequest) =>
      apiFetch('/departments', managedDepartmentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDepartmentRequest }) =>
      apiFetch(`/departments/${id}`, managedDepartmentSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/departments/${id}`, okResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTeamRequest) =>
      apiFetch('/teams', managedTeamSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTeamRequest }) =>
      apiFetch(`/teams/${id}`, managedTeamSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/teams/${id}`, okResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId ? teamMembersQueryKey(teamId) : ['teams', 'members', 'missing'],
    queryFn: () => apiFetch(`/teams/${teamId}/members`, teamMembersResponseSchema),
    enabled: Boolean(teamId),
  });
}

export function useAddTeamMember(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddTeamMemberRequest) =>
      apiFetch(`/teams/${teamId}/members`, teamMembersResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(teamMembersQueryKey(teamId), data);
      await invalidateOrgCaches(queryClient);
    },
  });
}

export function useRemoveTeamMember(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/teams/${teamId}/members/${userId}`, teamMembersResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(teamMembersQueryKey(teamId), data);
      await invalidateOrgCaches(queryClient);
    },
  });
}
